import type { QueryClient, UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { createTagV1WorkspacesWorkspaceIdTagsPost as createTag } from '../../generated';
import {
  type DetachTagV1WorkspacesWorkspaceIdAssetsImagesImageIdTagsTagIdDeleteError as DetachTagError,
  type TagRef,
  attachTagV1WorkspacesWorkspaceIdAssetsImagesImageIdTagsTagIdPut as attachTag,
  detachTagV1WorkspacesWorkspaceIdAssetsImagesImageIdTagsTagIdDelete as detachTag,
} from '../../generated';
import { APIError, getAPIErrorCode } from '../../utils';
import { refetchImagePayloads, serializeAssetFavoriteRequest } from '../set-asset-favorite/set-asset-favorite';

export const MAX_TAGS_PER_IMAGE = 8;

export const MAX_TAG_NAME_LENGTH = 50;

const ADD_IMAGE_TAG_MUTATION_KEY = ['AddImageTag'] as const;
const REMOVE_IMAGE_TAG_MUTATION_KEY = ['RemoveImageTag'] as const;

export function normalizeTagName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function getImageTags(image: { tags?: TagRef[] | null }): TagRef[] {
  return image.tags ?? [];
}

function sortTags(tags: TagRef[]): TagRef[] {
  return [...tags].sort((a, b) => a.name.localeCompare(b.name));
}

function getExistingTagId(error: unknown): string | undefined {
  if (!(error instanceof APIError) || getAPIErrorCode(error) !== 'TAG_ALREADY_EXISTS') {
    return undefined;
  }
  try {
    const existingTagId = JSON.parse(error.message)?.extra?.existing_tag_id;
    return typeof existingTagId === 'string' ? existingTagId : undefined;
  } catch {
    return undefined;
  }
}

function refreshTagLists(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['ListTags'] });
}

function serializeImageTagRequest<T>(workspaceId: string, imageId: string, request: () => Promise<T>) {
  return serializeAssetFavoriteRequest(`tags:${workspaceId}:${imageId}`, request);
}

export interface AddImageTagMutationVariables {
  workspace_id: string;
  image_id: string;
  tag: TagRef | { name: string };
}

export type ImageTagsMutation = TagRef[];

export const useAddImageTagMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<ImageTagsMutation, TError, AddImageTagMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<ImageTagsMutation, TError, AddImageTagMutationVariables, TContext>({
    mutationKey: ADD_IMAGE_TAG_MUTATION_KEY,
    mutationFn: ({ workspace_id, image_id, tag }) =>
      serializeImageTagRequest(workspace_id, image_id, async () => {
        let tagId = 'id' in tag ? tag.id : undefined;
        if (!tagId) {
          try {
            const { data } = await createTag({ path: { workspace_id }, body: { name: tag.name }, throwOnError: true });
            tagId = data.id;
          } catch (error) {
            tagId = getExistingTagId(error);
            if (!tagId) throw error;
          }
        }
        const { data } = await attachTag({ path: { workspace_id, image_id, tag_id: tagId }, throwOnError: true });
        return data;
      }),
    ...options,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context);
      refreshTagLists(queryClient);
      return refetchImagePayloads(queryClient, error);
    },
  });
};

export interface RemoveImageTagMutationVariables {
  workspace_id: string;
  image_id: string;
  tag: TagRef;
}

export const useRemoveImageTagMutation = <TError = DetachTagError, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<ImageTagsMutation, TError, RemoveImageTagMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<ImageTagsMutation, TError, RemoveImageTagMutationVariables, TContext>({
    mutationKey: REMOVE_IMAGE_TAG_MUTATION_KEY,
    mutationFn: ({ workspace_id, image_id, tag }) =>
      serializeImageTagRequest(workspace_id, image_id, async () => {
        const { data } = await detachTag({ path: { workspace_id, image_id, tag_id: tag.id }, throwOnError: true });
        return data;
      }),
    ...options,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context);
      refreshTagLists(queryClient);
      return refetchImagePayloads(queryClient, error);
    },
  });
};

interface PendingTagChange {
  imageId: string;
  tag: TagRef;
  isAdded: boolean;
  submittedAt: number;
}

const pendingTagId = (name: string) => `pending:${normalizeTagName(name)}`;

export function useImageTags() {
  const pendingAdds = useMutationState({
    filters: { mutationKey: ADD_IMAGE_TAG_MUTATION_KEY, status: 'pending' },
    select: (mutation): PendingTagChange | undefined => {
      const variables = mutation.state.variables as AddImageTagMutationVariables | undefined;
      if (!variables) return undefined;
      const { tag } = variables;
      const ref = 'id' in tag ? tag : { id: pendingTagId(tag.name), name: normalizeTagName(tag.name) };
      return { imageId: variables.image_id, tag: ref, isAdded: true, submittedAt: mutation.state.submittedAt };
    },
  });
  const pendingRemoves = useMutationState({
    filters: { mutationKey: REMOVE_IMAGE_TAG_MUTATION_KEY, status: 'pending' },
    select: (mutation): PendingTagChange | undefined => {
      const variables = mutation.state.variables as RemoveImageTagMutationVariables | undefined;
      return variables
        ? { imageId: variables.image_id, tag: variables.tag, isAdded: false, submittedAt: mutation.state.submittedAt }
        : undefined;
    },
  });

  const changes = React.useMemo(
    () =>
      [...pendingAdds, ...pendingRemoves]
        .filter((change): change is PendingTagChange => !!change)
        .sort((a, b) => a.submittedAt - b.submittedAt),
    [pendingAdds, pendingRemoves],
  );

  const tagsOf = React.useCallback(
    (image: { id: string; tags?: TagRef[] | null }): TagRef[] => {
      const byName = new Map(getImageTags(image).map((tag) => [tag.name, tag]));
      for (const change of changes) {
        if (change.imageId !== image.id) continue;
        if (change.isAdded) {
          if (!byName.has(change.tag.name)) byName.set(change.tag.name, change.tag);
        } else {
          byName.delete(change.tag.name);
        }
      }
      return sortTags([...byName.values()]);
    },
    [changes],
  );

  return { tagsOf };
}
